/**
 * /pricing — Transparent pricing page.
 *
 * Per stakeholder direction (2026-07-23), pricing is presented as a philosophy
 * rather than a menu of fixed prices — because every treatment is
 * individualized after a provider evaluation. What visitors need to know:
 *   1. Consultation fee is set upfront
 *   2. Treatment cost varies based on the individualized medical plan
 *   3. Insurance coverage is limited but explained
 *   4. Financing / payment options exist
 */
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Info, HelpCircle } from "lucide-react";
import { ScheduleConsultationCTA } from "../components/ScheduleConsultationCTA";
import SEO from "../components/SEO";

const PHILOSOPHY_ITEMS = [
  { title: "Transparent Consultation Fee",
    body: "You'll know the cost of your provider consultation before you book. No hidden fees." },
  { title: "Individualized Treatment Plans",
    body: "Because every plan is customized to your medical history and goals, treatment cost varies by protocol. Your provider reviews all costs with you before initiating therapy." },
  { title: "Insurance and Payment",
    body: "Most peptide therapies are not covered by commercial insurance. We provide detailed cost breakdowns and support HSA/FSA where eligible. Payment plans are available for qualifying treatments." },
  { title: "No Direct-to-Consumer Sales",
    body: "MedicalPeptides.ai is a provider-led platform. All peptide therapy is dispensed through appropriate pharmacy channels only after a licensed provider determines it's clinically appropriate for you." },
];

const FAQ = [
  { q: "How much does a consultation cost?",
    a: "Consultation fees are confirmed at the time of booking. Please schedule a consultation to see current pricing for your visit type." },
  { q: "Is peptide therapy covered by insurance?",
    a: "Coverage varies. Most peptide therapies are considered non-formulary or elective, so they are typically not covered by commercial insurance or Medicare. Your provider and our care team will help you understand the specific costs of any recommended plan." },
  { q: "Do you offer memberships?",
    a: "We are exploring membership options that bundle consultation, monitoring visits, and lab work at a predictable monthly cost. Ask your provider about current options during your consultation." },
  { q: "Are there financing options?",
    a: "Yes — for qualifying treatments, we offer payment plans through third-party healthcare-financing partners. Details are provided during your care coordination call after your consultation." },
];

export default function Pricing() {
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
      <SEO
        title="Peptide Therapy Pricing | Medical Peptides"
        description="Transparent pricing philosophy for provider-led peptide therapy. Consultation fees, treatment cost framework, insurance coverage, and payment options explained."
        canonical="https://medicalpeptides.ai/pricing"
      />
      <header className="text-center mb-10">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-3">
          Pricing
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mb-4">
          Transparent pricing, individualized care.
        </h1>
        <p className="max-w-2xl mx-auto text-lg text-stone-700 leading-relaxed">
          Because every peptide-therapy plan is personalized to your medical
          history and goals, treatment cost varies. What doesn't vary: our
          commitment to explaining every dollar before you commit.
        </p>
        <div className="mt-7">
          <ScheduleConsultationCTA location="pricing-page-hero" size="lg" />
        </div>
      </header>

      {/* Philosophy grid */}
      <section className="grid md:grid-cols-2 gap-4 mb-14">
        {PHILOSOPHY_ITEMS.map((it) => (
          <div key={it.title} className="rounded-2xl border border-stone-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={18} className="text-[color:var(--vv-teal)]" />
              <h3 className="text-lg font-semibold">{it.title}</h3>
            </div>
            <p className="text-stone-700 text-sm leading-relaxed">{it.body}</p>
          </div>
        ))}
      </section>

      {/* Trust framing */}
      <section className="rounded-3xl bg-[color:var(--vv-ink)] text-white p-8 md:p-12 mb-14">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--vv-teal)] font-semibold mb-2 flex items-center gap-2">
            <ShieldCheck size={14} /> Why we don't publish "list prices"
          </div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight mb-3">
            Peptide therapy isn't a commodity.
          </h2>
          <p className="text-stone-200 leading-relaxed mb-5">
            The right protocol for you depends on your goals, medical history,
            labs, and provider assessment. Publishing a fixed price for a
            specific peptide would be misleading — the exact protocol,
            duration, and dispensing pharmacy channel are all determined during
            your care plan. When you know your plan, you'll know your cost.
          </p>
          <ScheduleConsultationCTA location="pricing-page-trust" variant="gold" size="lg" />
        </div>
      </section>

      {/* FAQ */}
      <section className="mb-14">
        <h2 className="font-display text-3xl md:text-4xl mb-6 flex items-center gap-3">
          <HelpCircle size={28} className="text-[color:var(--vv-teal)]" />
          Frequently asked pricing questions
        </h2>
        <div className="space-y-3">
          {FAQ.map((f) => (
            <details key={f.q} className="group rounded-2xl border border-stone-200 bg-white">
              <summary className="cursor-pointer flex items-center justify-between p-5 font-medium text-stone-900">
                <span>{f.q}</span>
                <Info size={18} className="text-stone-400 group-open:rotate-180 transition-transform" />
              </summary>
              <p className="px-5 pb-5 text-stone-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Compliance blurb */}
      <p className="text-xs text-stone-500 text-center max-w-3xl mx-auto">
        Prices, memberships, and financing options are subject to change and
        depend on the individualized treatment plan approved by your provider.
        Nothing on this page is a quote, promise, or guarantee of specific
        pricing. Please read our{" "}
        <Link to="/terms-of-use" className="underline">terms</Link> and{" "}
        <Link to="/medical-disclaimer" className="underline">medical disclaimer</Link>.
      </p>
    </div>
  );
}
